import type { ZodType } from "zod";
import { ErroDaApi } from "./erro-da-api";

export type OpcoesDaChamada = {
  readonly metodo?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  readonly corpo?: unknown;
  /** Lista vira parametro repetido: `filtro=a&filtro=b`. */
  readonly consulta?: Readonly<Record<string, string | number | readonly string[] | undefined>>;
  /** Chave de idempotencia. Obrigatoria em escrita, gerada aqui se omitida. */
  readonly chaveDeIdempotencia?: string;
  readonly sinal?: AbortSignal;
};

export type FonteDeToken = () => string | null;

/**
 * Cliente HTTP da aplicacao. Ponto unico de saida para a API.
 *
 * Concentra quatro coisas que, espalhadas, sempre divergem:
 *
 * 1. O token vai em TODA requisicao. Nao existe caminho de chamada que esqueca,
 *    porque nao existe outro caminho.
 * 2. Erro sempre vira `ErroDaApi` com o Problem Details preservado.
 * 3. Escrita ganha `Idempotency-Key` automaticamente — a API exige, e depender
 *    de cada tela lembrar produziria 422 intermitente.
 * 4. A resposta e validada com o schema Zod do contrato. Se a API mudar o
 *    formato, o erro aparece aqui com o campo, e nao como `undefined` no meio
 *    da renderizacao.
 */
export class ClienteHttp {
  constructor(
    private readonly urlBase: string,
    private readonly obterToken: FonteDeToken,
    private readonly buscar: typeof fetch = globalThis.fetch.bind(globalThis),
    /**
     * Chamado em todo 401: a sessao expirou ou foi revogada no servidor. Quem
     * compoe a aplicacao usa isso para voltar ao login, em vez de cada tela
     * tratar "nao autorizado" do seu jeito.
     */
    private readonly aoPerderAutenticacao: () => void = () => {},
  ) {}

  async requisitar<T>(
    caminho: string,
    schema: ZodType<T>,
    opcoes: OpcoesDaChamada = {},
  ): Promise<T> {
    const metodo = opcoes.metodo ?? "GET";
    const token = this.obterToken();
    const cabecalhos: Record<string, string> = { accept: "application/json" };

    if (token) cabecalhos.authorization = `Bearer ${token}`;
    if (opcoes.corpo !== undefined) cabecalhos["content-type"] = "application/json";
    if (metodo !== "GET") {
      cabecalhos["idempotency-key"] =
        opcoes.chaveDeIdempotencia ?? crypto.randomUUID();
    }

    let resposta: Response;
    try {
      resposta = await this.buscar(this.montarUrl(caminho, opcoes.consulta), {
        method: metodo,
        headers: cabecalhos,
        // O cookie de sessao vai sozinho para a mesma origem. Se a API passar a
        // morar em outro dominio, troque para "include" e ligue credentials no CORS.
        credentials: "same-origin",
        ...(opcoes.corpo !== undefined ? { body: JSON.stringify(opcoes.corpo) } : {}),
        ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
      });
    } catch (causa) {
      throw ErroDaApi.deRede(causa);
    }

    const texto = await resposta.text();
    const corpo = texto.length > 0 ? seguroJson(texto) : null;

    if (!resposta.ok) {
      if (resposta.status === 401) this.aoPerderAutenticacao();
      throw ErroDaApi.deResposta(resposta.status, corpo);
    }

    const validado = schema.safeParse(corpo);
    if (!validado.success) {
      throw new ErroDaApi(
        resposta.status,
        null,
        `A resposta de ${caminho} nao corresponde ao contrato: ${validado.error.issues
          .map((i) => `${i.path.join(".") || "(raiz)"} ${i.message}`)
          .join("; ")}`,
      );
    }
    return validado.data;
  }

  private montarUrl(
    caminho: string,
    consulta: OpcoesDaChamada["consulta"],
  ): string {
    const parametros = new URLSearchParams();
    for (const [chave, valor] of Object.entries(consulta ?? {})) {
      if (Array.isArray(valor)) for (const item of valor) parametros.append(chave, item);
      else if (valor !== undefined && valor !== "") parametros.set(chave, String(valor));
    }
    const query = parametros.toString();
    return `${this.urlBase}${caminho}${query ? `?${query}` : ""}`;
  }
}

function seguroJson(texto: string): unknown {
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}
